import type { Cue } from '@core/model/cue.ts';
import type { TrackList } from '@core/model/track.ts';
import { parseCues } from '@core/parsers/index.ts';
import {
  injectYoutubeCreatorEnhancedTracks,
  selectionConverterCaptionTracks,
  type YoutubeCaptionTrack,
} from '@core/selection.ts';
import {
  normalizeSubtitleOptimizeModel,
  resolveEnhanceEndpoint,
} from '@core/alignment/subtitleOptimizeModel.ts';
import type { SubtitleProvider } from '../../ports/SubtitleProvider.ts';
import {
  fetchYouTubeCaptions,
  fetchYouTubeMetadata,
  type SoraAuthOpts,
  type SoraCaptionTrack,
  type SoraClientOptions,
} from '../net/soraClient.ts';
import {
  CaptionJobCancelledError,
  createMemberServiceInfoCue,
  getMemberEnhanceEpoch,
  pollMemberJobUntilDone,
  uploadAsrBridgePayload,
} from '../net/memberJobs.ts';
import {
  serviceDismissText,
  serviceDownloadingText,
  serviceEnhancedSuffix,
  serviceGeneratingText,
  serviceLoadingText,
} from '../../lib/serviceStatusMessages.ts';

export type SoraSubtitleProviderOptions = SoraClientOptions & {
  /** When true, inject type-2 creator-enhanced rows (subtitleOptimize). */
  shouldInjectCreatorEnhanced?: () => boolean;
  /** AI Segmentation model: auto | small | large. */
  getOptimizeModel?: () => string | undefined;
};

/** Matches extension i18n `web_service_whisper_selection_title` (not server stub copy). */
const WHISPER_SELECTION_TITLE = 'Generate subtitles';

/**
 * Server enhanced clones omit `languageCode`. injectService always clones the
 * primary (first) ASR and appends it at the end — never bind a later ASR row.
 */
function priorAsrLanguageCode(
  tracks: SoraCaptionTrack[],
  _fromIndex: number,
): string | undefined {
  return tracks.find((t) => t.kind === 'asr' && t.languageCode && !t.enhanced)
    ?.languageCode
    ?? tracks.find((t) => t.kind === 'asr' && t.languageCode)?.languageCode;
}

function toYoutubeCaptionTrack(track: SoraCaptionTrack): YoutubeCaptionTrack {
  return {
    name: { simpleText: track.name?.simpleText ?? 'Track' },
    languageCode: track.languageCode,
    kind: track.kind,
    enhanced: track.enhanced,
    creatorEnhanced: track.creatorEnhanced,
    whisper: track.whisper,
    restored: track.restored,
    vssId: track.vssId,
    downloadable: track.downloadable,
    baseUrl: track.baseUrl,
    asrBaseUrl: track.asrBaseUrl,
  };
}

type TimedtextPayload = {
  json3: unknown;
  kind: 'asr' | 'normal';
};

/**
 * After DO/Sora metadata (yt-caption-kit), match extension YouTube flow:
 * - normal/ASR: fetch track `baseUrl` directly (kit signed URL; no page pot)
 * - enhanced: download ASR → POST asr-cache → enhance job (guest v4 / member v)
 * - creator-enhanced: dual fetch → upload raw pair → server map → enhance
 *   (no same-lang ASR: upload raw creator → server synth timing → enhance)
 * - whisper: member job v2
 *
 * `/captions` remains a fallback if direct timedtext fails.
 * Do NOT re-inject enhanced/whisper — server metadata already does.
 * Creator-enhanced is client-injected (keeps languageCode).
 */
export class SoraSubtitleProvider implements SubtitleProvider {
  readonly id = 'sora';
  readonly label = 'YouTube';
  private readonly opts: SoraSubtitleProviderOptions;
  private readonly tracksByVideo = new Map<string, SoraCaptionTrack[]>();

  constructor(options: SoraSubtitleProviderOptions = {}) {
    this.opts = options;
  }

  async listTracks(videoId: string): Promise<TrackList> {
    const payload = await fetchYouTubeMetadata(videoId, this.opts);
    const serverTracks = payload.metadata.captionTracks ?? [];
    // Client inject only creator-enhanced; keep languageCode (unlike server enhanced).
    const mapped = serverTracks.map(toYoutubeCaptionTrack);
    const withCreator =
      this.opts.shouldInjectCreatorEnhanced?.() === true
        ? injectYoutubeCreatorEnhancedTracks(mapped, {
            enhancedSuffix: serviceEnhancedSuffix(),
          })
        : mapped;
    const captionTracks = withCreator.map((t) => ({
      ...t,
      name: t.name,
    })) as SoraCaptionTrack[];
    this.tracksByVideo.set(videoId, captionTracks);

    // Prefer core converter (extension popup semantics: name = simpleText).
    // Fall back to server selections only if captionTracks missing.
    // Server injectService strips languageCode from enhanced clones; extension
    // injectYoutubeService keeps it. Restore from the preceding ASR row so
    // pickPreferredTrackValue can match MRU codes (e.g. 'en') after reload.
    const fromCore = selectionConverterCaptionTracks(
      captionTracks.map((track, index) => ({
        name: { simpleText: track.name?.simpleText ?? 'Track' },
        languageCode:
          track.languageCode
          ?? (track.enhanced || track.vssId === 'enhanced'
            ? priorAsrLanguageCode(captionTracks, index)
            : undefined),
        kind: track.kind,
        enhanced: track.enhanced,
        creatorEnhanced: track.creatorEnhanced,
        whisper: track.whisper,
        vssId: track.vssId,
        downloadable: track.downloadable,
        baseUrl: track.baseUrl,
        asrBaseUrl: track.asrBaseUrl,
      })),
    );

    const subtitles = (fromCore.length > 0
      ? fromCore
      : (payload.selections.subtitles ?? [])
    ).map((row, index) => {
      const track = captionTracks[index];
      const isEnhanced = Boolean(track?.enhanced || track?.vssId === 'enhanced');
      const isCreatorEnhanced = Boolean(
        track?.creatorEnhanced || track?.vssId === 'creator-enhanced',
      );
      const isWhisper = Boolean(track?.whisper || track?.vssId === 'whisper');
      // Remap server "… (Members-only feature)" to extension whisper menu title.
      const name = isWhisper ? WHISPER_SELECTION_TITLE : row.name;
      let code = row.code ?? '';
      if (!code && isEnhanced) {
        code = priorAsrLanguageCode(captionTracks, index) ?? '';
      }
      return {
        ...row,
        name,
        code,
        downloadable: row.downloadable !== false,
        enhanced: isEnhanced,
        creatorEnhanced: isCreatorEnhanced,
        whisper: isWhisper,
        kind: track?.kind,
        // Extension: only Whisper is membership-gated; Enhanced is free (trial) / member (full).
        memberOnly: isWhisper,
      };
    });

    return {
      subtitles,
      translations: payload.selections.translations ?? [],
    };
  }

  async downloadTrack(
    videoId: string,
    trackId: string,
    opts?: {
      onProgress?: (message: string) => void;
      shouldContinue?: () => boolean;
    },
  ): Promise<Cue[]> {
    const trackIndex = Number(trackId);
    if (!Number.isInteger(trackIndex)) {
      throw new Error(`Invalid track id: ${trackId}`);
    }

    const cached = this.tracksByVideo.get(videoId)?.[trackIndex];
    const auth = this.opts.getAuth?.() ?? { isMember: false, userId: '' };

    if (cached?.creatorEnhanced || cached?.vssId === 'creator-enhanced') {
      return this.downloadCreatorEnhancedTrack(
        videoId,
        trackIndex,
        cached,
        auth,
        opts?.onProgress,
        opts?.shouldContinue,
      );
    }
    if (cached?.enhanced || cached?.vssId === 'enhanced') {
      return this.downloadEnhancedTrack(
        videoId,
        trackIndex,
        cached,
        auth,
        opts?.onProgress,
        opts?.shouldContinue,
      );
    }
    if (cached?.whisper || cached?.vssId === 'whisper') {
      opts?.onProgress?.(serviceLoadingText());
      return this.downloadWhisperTrack(
        videoId,
        auth,
        opts?.onProgress,
        opts?.shouldContinue,
      );
    }

    opts?.onProgress?.(serviceDownloadingText());
    const { json3, kind } = await this.fetchTimedtextJson3(videoId, trackIndex, cached);
    const parseKind =
      kind === 'asr' || cached?.kind === 'asr' ? 'json3-asr' : 'json3-normal';
    return parseCues(parseKind, json3 as object);
  }

  /** Extension-parity: prefer kit `baseUrl`; fall back to Sora `/captions`. */
  private async fetchTimedtextJson3(
    videoId: string,
    trackIndex: number,
    track?: SoraCaptionTrack,
  ): Promise<TimedtextPayload> {
    if (track?.baseUrl) {
      try {
        const res = await (this.opts.fetchImpl ?? fetch)(track.baseUrl);
        if (res.ok) {
          const json3 = await res.json();
          if (json3 && typeof json3 === 'object') {
            return {
              json3,
              kind: track.kind === 'asr' ? 'asr' : 'normal',
            };
          }
        }
      } catch {
        // fall through to Sora captions proxy
      }
    }

    return fetchYouTubeCaptions(videoId, trackIndex, this.opts);
  }

  /**
   * Enhanced clones often keep ASR `baseUrl`; if not, use the primary ASR
   * (injectService clones find-first and appends at list end).
   */
  private findAsrSource(
    videoId: string,
    enhancedIndex: number,
  ): { track: SoraCaptionTrack; index: number } | undefined {
    const tracks = this.tracksByVideo.get(videoId) ?? [];
    const selected = tracks[enhancedIndex];
    if (selected?.kind === 'asr' && selected.baseUrl) {
      return { track: selected, index: enhancedIndex };
    }
    const index = tracks.findIndex(
      (t) => t.kind === 'asr' && Boolean(t.baseUrl) && !t.enhanced && !t.whisper,
    );
    return index >= 0 ? { track: tracks[index]!, index } : undefined;
  }

  private resolveEnhanceEndpointOrGate(auth: SoraAuthOpts): 'v' | 'v4' | 'member_only' {
    return resolveEnhanceEndpoint(
      normalizeSubtitleOptimizeModel(this.opts.getOptimizeModel?.()),
      Boolean(auth.isMember),
    );
  }

  private async downloadWhisperTrack(
    videoId: string,
    auth: SoraAuthOpts,
    onProgress?: (message: string) => void,
    shouldContinue?: () => boolean,
  ): Promise<Cue[]> {
    if (!auth.isMember) {
      throw new Error(serviceDismissText('members_only'));
    }
    return pollMemberJobUntilDone({
      endpoint: 'v2',
      videoId,
      isMember: Boolean(auth.isMember),
      userId: auth.userId,
      userToken: auth.userToken,
      fetchImpl: this.opts.fetchImpl,
      baseUrl: this.opts.baseUrl,
      apiKey: this.opts.apiKey,
      onProgress,
      shouldContinue,
    });
  }

  private async downloadEnhancedTrack(
    videoId: string,
    enhancedIndex: number,
    _track: SoraCaptionTrack,
    auth: SoraAuthOpts,
    onProgress?: (message: string) => void,
    shouldContinue?: () => boolean,
  ): Promise<Cue[]> {
    const epoch = getMemberEnhanceEpoch();
    const alive = () =>
      epoch === getMemberEnhanceEpoch() && (!shouldContinue || shouldContinue());
    if (!alive()) {
      throw new CaptionJobCancelledError();
    }

    const endpointChoice = this.resolveEnhanceEndpointOrGate(auth);
    if (endpointChoice === 'member_only') {
      return createMemberServiceInfoCue(serviceDismissText('members_only'));
    }
    const endpoint = endpointChoice;

    // After gate: show Generating before upload (do not pre-flash this).
    onProgress?.(serviceGeneratingText());

    // Fail-closed like extension: no ASR / failed upload → do not open enhance job.
    await this.ensureAsrCacheForEnhance(videoId, enhancedIndex, auth);
    if (!alive()) {
      throw new CaptionJobCancelledError();
    }

    return pollMemberJobUntilDone({
      endpoint,
      videoId,
      isMember: Boolean(auth.isMember),
      userId: auth.userId,
      userToken: auth.userToken,
      fetchImpl: this.opts.fetchImpl,
      baseUrl: this.opts.baseUrl,
      apiKey: this.opts.apiKey,
      onProgress,
      shouldContinue: alive,
    });
  }

  private async downloadCreatorEnhancedTrack(
    videoId: string,
    trackIndex: number,
    track: SoraCaptionTrack,
    auth: SoraAuthOpts,
    onProgress?: (message: string) => void,
    shouldContinue?: () => boolean,
  ): Promise<Cue[]> {
    const epoch = getMemberEnhanceEpoch();
    const alive = () =>
      epoch === getMemberEnhanceEpoch() && (!shouldContinue || shouldContinue());
    if (!alive()) {
      throw new CaptionJobCancelledError();
    }

    const endpointChoice = this.resolveEnhanceEndpointOrGate(auth);
    if (endpointChoice === 'member_only') {
      return createMemberServiceInfoCue(serviceDismissText('members_only'));
    }
    const endpoint = endpointChoice;
    const lc = track.languageCode ?? '';

    onProgress?.(serviceGeneratingText());

    await this.ensureCreatorAsrCache(videoId, trackIndex, track, auth);
    if (!alive()) {
      throw new CaptionJobCancelledError();
    }
    return pollMemberJobUntilDone({
      endpoint,
      videoId,
      asrSource: 'creator',
      lc,
      isMember: Boolean(auth.isMember),
      userId: auth.userId,
      userToken: auth.userToken,
      fetchImpl: this.opts.fetchImpl,
      baseUrl: this.opts.baseUrl,
      apiKey: this.opts.apiKey,
      onProgress,
      shouldContinue: alive,
    });
  }

  /** Download ASR timedtext and POST `/asr-cache` before createJob. */
  private async ensureAsrCacheForEnhance(
    videoId: string,
    enhancedIndex: number,
    auth: SoraAuthOpts,
  ): Promise<void> {
    const source = this.findAsrSource(videoId, enhancedIndex);
    if (!source) {
      throw new Error(serviceDismissText('source'));
    }

    const { json3 } = await this.fetchTimedtextJson3(videoId, source.index, source.track);
    if (!json3 || typeof json3 !== 'object') {
      throw new Error(serviceDismissText('source'));
    }
    const events = (json3 as { events?: unknown }).events;
    if (!Array.isArray(events) || events.length === 0) {
      throw new Error(serviceDismissText('source'));
    }

    try {
      await uploadAsrBridgePayload(
        videoId,
        json3,
        source.track.languageCode ?? '',
        this.opts.fetchImpl,
        auth,
        this.opts.baseUrl,
        this.opts.apiKey,
      );
    } catch (error) {
      console.log(error);
      throw new Error(serviceDismissText('source'));
    }
  }

  /**
   * Creator enhance input:
   * - same-lang ASR (`asrBaseUrl`): dual-fetch pair → server map
   * - no ASR: upload raw creator json3 → server synth timing
   */
  private async ensureCreatorAsrCache(
    videoId: string,
    _trackIndex: number,
    track: SoraCaptionTrack,
    auth: SoraAuthOpts,
  ): Promise<void> {
    if (!track.baseUrl) {
      throw new Error(serviceDismissText('source'));
    }
    const lc = track.languageCode ?? '';

    if (!track.asrBaseUrl) {
      const fetchImpl = this.opts.fetchImpl ?? fetch.bind(globalThis);
      let creatorJson: unknown;
      try {
        const creatorRes = await fetchImpl(track.baseUrl);
        if (!creatorRes.ok) throw new Error('timedtext fetch failed');
        creatorJson = await creatorRes.json();
      } catch (error) {
        console.log(error);
        throw new Error(serviceDismissText('source'));
      }
      if (!creatorJson || typeof creatorJson !== 'object') {
        throw new Error(serviceDismissText('source'));
      }
      const creatorEvents = (creatorJson as { events?: unknown }).events;
      if (!Array.isArray(creatorEvents) || creatorEvents.length === 0) {
        throw new Error(serviceDismissText('source'));
      }
      try {
        await uploadAsrBridgePayload(
          videoId,
          { creator: creatorJson },
          lc,
          this.opts.fetchImpl,
          auth,
          this.opts.baseUrl,
          this.opts.apiKey,
          'creator',
        );
      } catch (error) {
        console.log(error);
        throw new Error(serviceDismissText('source'));
      }
      return;
    }

    const fetchImpl = this.opts.fetchImpl ?? fetch.bind(globalThis);
    let creatorJson: unknown;
    let asrJson: unknown;
    try {
      const [creatorRes, asrRes] = await Promise.all([
        fetchImpl(track.baseUrl),
        fetchImpl(track.asrBaseUrl),
      ]);
      if (!creatorRes.ok || !asrRes.ok) {
        throw new Error('timedtext fetch failed');
      }
      creatorJson = await creatorRes.json();
      asrJson = await asrRes.json();
    } catch (error) {
      console.log(error);
      throw new Error(serviceDismissText('source'));
    }
    if (!creatorJson || typeof creatorJson !== 'object' || !asrJson || typeof asrJson !== 'object') {
      throw new Error(serviceDismissText('source'));
    }

    const creatorEvents = (creatorJson as { events?: unknown }).events;
    const asrEvents = (asrJson as { events?: unknown }).events;
    if (!Array.isArray(creatorEvents) || creatorEvents.length === 0) {
      throw new Error(serviceDismissText('source'));
    }
    if (!Array.isArray(asrEvents) || asrEvents.length === 0) {
      throw new Error(serviceDismissText('source'));
    }

    try {
      await uploadAsrBridgePayload(
        videoId,
        { creator: creatorJson, asr: asrJson },
        lc,
        this.opts.fetchImpl,
        auth,
        this.opts.baseUrl,
        this.opts.apiKey,
        'creator',
      );
    } catch (error) {
      console.log(error);
      throw new Error(serviceDismissText('source'));
    }
  }
}
